import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, CircularProgress, Container, Grid, Typography, Popover } from '@mui/material';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import AuthWrapper from 'src/components/AuthWrapper';
import BarsComponent from 'src/components/bar/BarsComponent';
import DekstopPoup from 'src/components/CareerDetailsPopup/DekstopPoup';
import MobilePopup from 'src/components/CareerDetailsPopup/MobilePopup';
import { setAlert, useGetClgBySlugQuery } from 'src/layouts/main/MainLayoutSlice';
import { currencyFormat } from 'src/utils';
import axiosInstance from 'src/utils/axiosInstance';
import RadarChartComponent from 'src/components/RadarChart/RadarChartComponent';
import { skillLevels } from 'src/assets/data/Dummy';
import { getRegionBySlug } from '../CareerHistory/CareerHistorySlice';
import { useGetCareersReskillTimeMutation } from '../Reskill_Flow/ReskillSlice';
import {
  useCareerNCoursesDetailsMutation,
  useGetSkilldarChartDataMutation,
} from '../Upskill_Flow/UpskillSlice';
import ChatBot from './ChatBot';
import VideoComponent from './VideoComponent';
import WidgetStepper from './WidgetStepper';
import WidgetRadarChart from './WidgetRadarChart';

const careerHistoryId = ['64ad551627a3095a17a94970', '64957bc29d014f9c6ffd6d05'];

const Widget = () => {
  const widgetsSteps = [
    {
      label: 'Think Careers',
      stepIndex: 0,
    },
    {
      label: 'Watch Our Imersive Video',
      stepIndex: 1,
    },
    {
      label: 'Chat with our AI BOT',
      stepIndex: 2,
    },
  ];
  const [searchParams, setSearchParams] = useSearchParams();
  const stepFromURL = parseInt(searchParams.get('step'), 10) || 0;
  const [currentStep, setCurrentStep] = useState(stepFromURL);

  const collegeName = searchParams.get('collegeName');
  const courseId = searchParams.get('courseId');
  const widgetType = searchParams.get('widgetType');
  // autoHeight=true in the widget link: the widget is as tall as its content (instead of filling its frame) and tells the page it
  // sits on how tall that is, so the page can resize the frame (see docs/widget-embed.md)
  const autoHeight = ['true', '1'].includes(String(searchParams.get('autoHeight')).toLowerCase());
  const handleStepChange = (stepIndex) => {
    setCurrentStep(stepIndex);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set('step', stepIndex);
      return newParams;
    });
  };
  const staticURL = 'https://player.vimeo.com/video/669377715';
  const [hasError, setHasError] = useState(false);
  const [careersReskillTime, setCareersReskillTime] = useState([]);
  const params = useParams();
  const cg_name = params?.cg_name;
  const hasErrorRef = useRef(false);
  const [getCareersReskillTime, status] = useGetCareersReskillTimeMutation();

  const {
    data: clgBySlug,
    error: slugError,
    isLoading: slugLoading,
    isError,
  } = useGetClgBySlugQuery(cg_name, {
    skip: !cg_name || hasErrorRef.current,
    retry: 0, // Important!
    refetchOnMountOrArgChange: false,
  });
  const dispatch = useDispatch();
  const [chartWidth, setChartWidth] = useState(820);
  const [chartHeight, setChartHeight] = useState('100%');
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);
  // autoHeight mode: the widget's own box, measured and reported to the page that embeds it
  const [rootElement, setRootElement] = useState(null);
  const autoHeightSent = useRef(0);

  useEffect(() => {
    if ((isError || slugError?.status === 'FETCH_ERROR') && !hasErrorRef.current) {
      hasErrorRef.current = true;
    }
  }, [isError, slugError, hasErrorRef]);
  // const careerIds = useMemo(() => {
  //   const widgetSearchParams = new URLSearchParams(window.location.search);
  //   return widgetSearchParams.get('career_ids')?.split(',') || [];
  // }, []);
  const careerurl = useMemo(() => {
    const widgetSearchParams = new URLSearchParams(window.location.search);
    return widgetSearchParams.get('url') || '';
  }, []);
  const [careerLoading, setCareerLoading] = useState(false);
  const [getUpskillRadar, upskillSkilldarData] = useGetSkilldarChartDataMutation();
  const [cachedRadarData, setCachedRadarData] = useState(null);
  const [isRadarLoading, setIsRadarLoading] = useState(false);
  const [widgetRadarData, setWidgetRadarData] = useState(null);

  const getSkilldarAverage = useMemo(() => async () => {
    if (cachedRadarData) {
      return cachedRadarData;
    }
  
    setIsRadarLoading(true);
  try {
    const careerIds = [];
    
    if (careersReskillTime?.sectorOrSubsector?.length > 0) {
      careersReskillTime.sectorOrSubsector.forEach(sector => {
        if (sector.careers) {
          sector.careers.forEach(career => {
            if (career.id) {
              careerIds.push(career.id);
            }
          });
        }
      });
    }
        if (careerIds.length === 0) {
      setIsRadarLoading(false);
      return null;
    }
    
    const response = await axiosInstance({
      url: 'frontend/getSkilldarAverage',
      method: 'POST',
      data: { careerIds }
    });
    
    const result = {
      data: response.data.data
    };
    
    setCachedRadarData(result);
    return result;
  } catch (error) {
    console.error('Error fetching radar data:', error);
    return null;
  } finally {
    setIsRadarLoading(false);
  }
  }, [cachedRadarData, careersReskillTime]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (careerurl) {
      const url =
        careerurl ||
        'https://www.howcollege.ac.uk/courses/business-and-management/4657/level-5-hnd-in-leadership-and-management-htq-full-time/';
      const fetchCareers = async () => {
        try {
          setCareerLoading(true);
          const response = await axiosInstance({
            url: `frontend/getWidgetCareers?url=${url}`,
            method: 'POST',
          });
          setCareersReskillTime(response.data?.data);
          // localdata set
          return response.data;
        } catch (error) {
          console.log('error get regions', error);
          dispatch(
            setAlert({
              open: true,
              msg: error.data?.msg ? `${error.status}, ${error.data.msg}` : 'Something went wrong',
            })
          );
          return error;
        } finally {
          setCareerLoading(false);
        }
      };

      // getCareersReskillTime(data)
      fetchCareers(url);
      // .then((payload) => setCareersReskillTime(payload))
      //   .catch((error) =>
      //     dispatch(
      //       setAlert({
      //         open: true,
      //         msg: error.data?.msg ? `${error.status}, ${error.data.msg}` : 'Something went wrong',
      //       })
      //     )
      //   );
    }
  }, [careerurl, dispatch]);
  const [loadedRadarData, setLoadedRadarData] = useState(false);
  const loadRadar = () => {
    setLoadedRadarData(true);
  };
  useEffect(() => {
    const handleWindowResize = () => {
      const width = window.innerWidth;
      setContainerWidth(width);
      
      if (width <= 1024 && width > 599) {
        setChartWidth('100%');
        setChartHeight('85%');
      } else if (width <= 599 && width > 399) {
        setChartWidth('100%');
        setChartHeight('60%');
      } else if (width <= 399) {
        setChartWidth('100%');
        setChartHeight('50%');
      } else {
        setChartWidth('99%');
        setChartHeight('100%');
      }
    };
    if (window.innerWidth <= 1024 && window.innerWidth > 599) {
      setChartWidth('100%');
      setChartHeight('85%');
    } else if (window.innerWidth <= 599 && window.innerWidth > 399) {
      setChartWidth('100%');
      setChartHeight('60%');
    } else if (window.innerWidth <= 399) {
      setChartWidth('100%');
      setChartHeight('50%');
    } else {
      setChartWidth('99%');
      setChartHeight('100%');
    }

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);
  const [showLabel, setShowLabel] = useState(false);
  const popupRef = useRef(null);
  const [popupChartWidth, setPopupChartWidth] = useState('100%');
  
  const closeLabel = () => {
    setShowLabel(false);
    document.body.style.overflow = 'unset';
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };
  const openLabel = () => {
    setShowLabel(true);
    if (typeof window !== 'undefined' && window.document) {
      document.body.style.overflow = 'unset';
      // window.scrollTo({
      //   top: 60,
      //   behavior: "smooth"
      // });
    }
  };
  const [anchorEl, setAnchorEl] = useState(null);
  const [slideNo, setSlideNo] = useState(1);
  const toggleShowSkilldarPopup = (event) => {
    setAnchorEl(event.currentTarget);
    setSlideNo(1);
  };
  // useEffect(() => {
  //   document.body.style.overflow = 'hidden';
  //   return () => {
  //     document.body.style.overflow = 'unset';
  //   };
  // }, []);
  const [careerNCoursesDetails, careerNCoursesDetailsStatus] = useCareerNCoursesDetailsMutation({});
  const [selectedCareerId, setSelectedCareerId] = useState(null);
  const [careerNCourseData, setCareerNCourseData] = useState();
  const [skillRadarPopup, setSkillRadarPopup] = useState(false);
  const [radarPopupData, setRadarPopupData] = useState(null);
  const [expanded, setExpanded] = useState(0);
  const [readMore, setReadMore] = useState(false);
  const [tabValue, setTabValue] = useState(1);
  const [careerNCoursesPopDetails, setCareerNCoursesPopDetails] = useState();
  const [openCareerDetailsPopup, setOpenCareerDetailsPopup] = useState(false);
  const handleChange = (e) => (event, isExpanded) => {
    setExpanded(isExpanded ? e : false);
  };
  const [careerDetailsError, setCareerDetailsError] = useState(false);
  const [careerDetailsRetry, setCareerDetailsRetry] = useState(0);
  // The details are fetched by the effect below, so opening a career only has to select it
  const showCareerDetailsPopup = (id) => {
    setCareerDetailsError(false);
    setSelectedCareerId(id);
    setOpenCareerDetailsPopup(true);
  };

  const chartRef = useRef(null);

  const [getSkillReportPopup, setGetSkillReportPopup] = useState(false);
  const [compareCareersState, setCompareCareersState] = useState([]);

  const [qualificationsChartData, setQualificationsChartData] = useState({});
  const [compareCareersData, setCompareCareersData] = useState([]);
  const [chartSize, setChartSize] = useState();
  const [reskillcareersListData, setReskillCareersListData] = useState();

  const [bubblechartData, setBubblechartData] = useState();

  const collegeDetails = useSelector((state) => state.mainLayout);
  const [onClickChartData, setOnClickChartData] = useState();
  const [anchorElChartData, setAnchorElChartData] = useState(null);

  const showChartDataPopup = (event) => {
    setAnchorElChartData(true);
  };
  const [careerChartNListData, setcareerChartNListData] = useState();

  const [regionalChartData, setRegionalChartData] = useState({});

  const chartDataDetails1 = {
    series: [],
    options: {
      chart: {
        type: 'bar',
        height: 350,
      },
      plotOptions: {
        bar: {
          columnWidth: '70%',
        },
      },
      dataLabels: {
        enabled: false,
      },
      colors: [`${collegeDetails.collegeDetails.primaryColor}`],
      yaxis: {
        min: 0,
        labels: {
          formatter: (val) => Number(val).toFixed(2).replace(/\.00$/, ''), // removes .00 if not needed
        },
      },
      xaxis: {
        convertedCatToNumeric: false,
      },
    },
  };
  const chartDataDetails2 = {
    series: [],
    options: {
      chart: {
        type: 'bar',
        height: 350,
      },
      plotOptions: {
        bar: {
          columnWidth: '70%',
        },
      },
      dataLabels: {
        enabled: false,
      },
      colors: [`${collegeDetails.collegeDetails.primaryColor}`],
      yaxis: {
        min: 0,
        labels: {
          formatter: (val) => Number(val).toFixed(2).replace(/\.00$/, ''), // removes .00 if not needed
        },
      },
      xaxis: {
        convertedCatToNumeric: false,
      },
    },
  };
  const HandleCloseSkillReportPopup = () => {
    setGetSkillReportPopup(false);
    // setTermsAndCondition(false)
  };
  const HandleCloseCareerDetailsPopup = () => {
    setOpenCareerDetailsPopup(false);
    setCareerNCoursesPopDetails(null);
    setExpanded(0);
    setReadMore(false);
  };
    const HandleOpenRadarPopup = async () => {
      try {
        const response = await getSkilldarAverage();
        setRadarPopupData(response.data);
        setSkillRadarPopup(true);
      } catch (error) {
        console.error('Error loading radar data:', error);
      }
    };
    
    const HandleCloseRadarPopup = () => {
      setSkillRadarPopup(false);
    };
  const ref = useRef(null);

  useEffect(() => {
    const Qualification =
      careerNCoursesPopDetails?.growthData?.QualificationGrowth?.chartData?.xaxis;
    const Regional = careerNCoursesPopDetails?.growthData?.regionalGrowth?.chartData?.xaxis;

    if (careerNCoursesPopDetails && careerNCoursesPopDetails.growthData) {
      // copies: the response is frozen by the store and the chart library adds fields to its series
      chartDataDetails1.series = JSON.parse(
        JSON.stringify(careerNCoursesPopDetails?.growthData?.regionalGrowth?.chartData?.series ?? [])
      );
      chartDataDetails1.options.xaxis = { ...Regional, convertedCatToNumeric: false };
      setRegionalChartData(chartDataDetails1);

      chartDataDetails2.series = JSON.parse(
        JSON.stringify(careerNCoursesPopDetails?.growthData?.QualificationGrowth?.chartData?.series ?? [])
      );
      chartDataDetails2.options.xaxis = { ...Qualification, convertedCatToNumeric: false };
      setQualificationsChartData(chartDataDetails2);
    }
  }, [careerNCoursesPopDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (careerNCourseData) {
      let newState;
      if (compareCareersState?.length > 0) {
        const isIdInArray = compareCareersState.includes(careerNCourseData.id);
        if (isIdInArray) {
          newState = {
            ...careerNCourseData,
            addToComparison: true,
          };
        } else {
          newState = {
            ...careerNCourseData,
            addToComparison: false,
          };
        }
      } else {
        newState = {
          ...careerNCourseData,
          addToComparison: false,
        };
      }
      setCareerNCoursesPopDetails(newState);
      if (newState?.courses?.length) {
        // setExpanded(newState?.courses[0].title)
      }
    }
  }, [careerNCourseData]); // eslint-disable-line react-hooks/exhaustive-deps
  const [regionData, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const regions = useSelector((state) => state.careerHistory);
  const buttonColor = regionData?.button?.bgColor;
  const buttonFontColor = regionData?.button?.color;
  const logo = regionData?.logo || '';
  const partnerLogos = regionData?.partnerLogos || [];
  const primaryColor = regionData?.primaryColor || '';
  const fontColor = regionData?.fontColor || '';
  const secondaryColor = regionData?.secondary;
  const bgImage = regionData?.bgImage || '';
  // Region of the college the widget is embedded for, e.g. "West Midlands"
  const regionName = regionData?.name || clgBySlug?.data?.region?.name || '';

  // The immersive video tab needs the college to allow it AND videos for the sector being viewed
  const sectorVideoUrls = careersReskillTime?.videoUrls || [];
  const showVideoTab = clgBySlug?.data?.widgetVideoEnabled !== false && sectorVideoUrls.length > 0;
  // Per-college setting: the Skilldar panel on the right of the widget
  const showSkilldar = clgBySlug?.data?.widgetShowSkilldar !== false;
  const showChatTab = clgBySlug?.data?.widgetShowChat !== false;
  const showPoweredBy = clgBySlug?.data?.widgetShowPoweredBy !== false;
  // Without the background image the widget also loses the space around it and fills the whole width
  const showBackground = clgBySlug?.data?.widgetShowBackground !== false;
  const customCss = clgBySlug?.data?.widgetCustomCss || '';
  // The site's global stylesheet forces 30px on each side of every container (with !important), so it has to be
  // overridden explicitly for the widget to fill the full width
  const framePadding = showBackground
    ? { px: 1, py: 2 }
    : { px: 0, py: 0, '&.MuiContainer-root': { paddingLeft: '0 !important', paddingRight: '0 !important' } };
  // wide screens use two columns; fixedLayout = they fill the frame's height and scroll inside (never in autoHeight mode)
  const fixedLayout = containerWidth > 1000 && !autoHeight;
  // The height set for this college under Widget options (College > Edit). Empty = the default: fill the frame the widget is in.
  // ?autoHeight=true in the address (content height) takes precedence over it.
  const heightSetting = Number(clgBySlug?.data?.widgetHeight);
  // The AI chat tab needs room for its messages and message box (measured: about 340px), so while it is shown the widget is
  // never shorter than 400px, whatever is saved. The college form and the server apply the same rule when saving.
  const minimumHeight = showChatTab ? 400 : 200;
  const validSetting = !autoHeight && Number.isInteger(heightSetting) && heightSetting >= 200 && heightSetting <= 3000;
  const configuredHeight = validSetting ? Math.max(heightSetting, minimumHeight) : null;
  const configuredHeightCss = configuredHeight ? `${configuredHeight}px` : null;
  // The tab area (careers, video, chat) is given a definite height to fill: the whole frame on wide screens, or the set height
  // on any screen. Otherwise (the stacked layout with no height set) it is as tall as its content, as before.
  const fillMain = fixedLayout || Boolean(configuredHeight);
  const fullFrameHeight = showBackground ? '98vh' : '100vh';
  let frameHeight = 'auto';
  if (fixedLayout) frameHeight = configuredHeightCss || fullFrameHeight;
  else if (configuredHeight) frameHeight = '100%';
  let frameMinHeight = autoHeight ? 'auto' : '100vh';
  if (configuredHeight) frameMinHeight = fixedLayout ? configuredHeightCss : 'auto';
  else if (fixedLayout) frameMinHeight = fullFrameHeight;
  // height of the widget's outer box: the set height, else the whole frame (wide), else its content (stacked)
  let rootHeight = 'auto';
  if (configuredHeightCss) rootHeight = configuredHeightCss;
  else if (fixedLayout) rootHeight = '100vh';

  // Tells the page the widget sits in how tall it is: the measured height in content-height mode, the set height otherwise.
  // The number is all that is sent; the page decides what to do with it (see docs/widget-embed.md).
  useEffect(() => {
    if (!rootElement || window.parent === window) return undefined;
    const post = (height) => {
      if (height > 0 && height !== autoHeightSent.current) {
        autoHeightSent.current = height;
        window.parent.postMessage({ source: 'thinkskills-widget', type: 'resize', height }, '*');
      }
    };
    if (configuredHeight) {
      post(configuredHeight);
      return undefined;
    }
    if (!autoHeight) return undefined;
    let frame = null;
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(() => { frame = null; post(Math.ceil(rootElement.getBoundingClientRect().height)); });
    };
    const observer = new ResizeObserver(schedule);
    observer.observe(rootElement);
    schedule();
    return () => {
      observer.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [autoHeight, configuredHeight, rootElement]);
  // Width of the left column: shares the row with the Skilldar panel, or takes all of it when that is hidden
  let leftColumnFlex = '0 0 auto';
  if (containerWidth > 1000) leftColumnFlex = showSkilldar ? '1 1 70%' : '1 1 100%';
  const widgetDataLoaded = !!clgBySlug && Array.isArray(careersReskillTime?.sectorOrSubsector);
  useEffect(() => {
    // A link that opens a tab that isn't shown (?step=1 video, ?step=2 chat) falls back to the first tab
    if (widgetDataLoaded && ((!showVideoTab && currentStep === 1) || (!showChatTab && currentStep === 2))) {
      handleStepChange(0);
    }
  }, [widgetDataLoaded, showVideoTab, showChatTab, currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!openCareerDetailsPopup || !selectedCareerId) return undefined;
    let ignore = false;
    setCareerDetailsError(false);
    careerNCoursesDetails({
      selectedCareerId,
      currentCareerIds: [selectedCareerId],
      courseUrl: careerurl,
      careerRegionId: tabValue === 0 ? 'national' : regionData?._id,
    })
      .unwrap()
      .then((payload) => {
        if (!ignore) setCareerNCourseData(payload);
      })
      .catch((error) => {
        if (ignore) return;
        setCareerDetailsError(true);
        dispatch(
          setAlert({
            open: true,
            msg: error.data?.msg ? `${error.status}, ${error.data.msg}` : 'Something went wrong',
          })
        );
      });
    // a response for a career/region that is no longer selected must not replace the current one
    return () => {
      ignore = true;
    };
  }, [
    tabValue,
    selectedCareerId,
    dispatch,
    openCareerDetailsPopup,
    careerNCoursesDetails,
    careerurl,
    regionData?._id,
    careerDetailsRetry,
  ]);
  useEffect(() => {
    const handleWindowResize = () => {
      setChartSize(chartRef?.current?.offsetWidth);
      setChartWidth(ref?.current?.offsetWidth);
    };
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);
  // The region (branding, colours, logos) comes from the college the widget is embedded for
  const regionSlug = clgBySlug?.data?.region?.slug;
  useEffect(() => {
    if (!regionSlug) return;
    setLoading(true);
    dispatch(getRegionBySlug(regionSlug));
  }, [dispatch, regionSlug]);
  useEffect(() => {
    if (regions?.regions) {
      setData(regions?.regions);
      if (!isEmpty(regionData)) {
        setLoading(false);
      }
    }
  }, [regions?.regions, regionData]);
  useEffect(() => {
    if (careerChartNListData) {
      const data = {
        series: careerChartNListData,
        options: {
          xaxis: {
            tickAmount: 20,
            type: 'category',
            min: 3,
            max: 60,
            title: {
              text: 'Transfer Window',
              style: {
                color: '#1f1f1f',
                fontSize: '16px',
                fontFamily: 'Public Sans,sans-serif',
              },
            },
            subTitle: {
              text: 'Average Salary',
              style: {
                color: '#1f1f1f',
                fontSize: '16px',
                fontFamily: 'Public Sans,sans-serif',
              },
            },
          },
          yaxis: {
            tickAmount: 20,
            max: 100000,
            min: 15000,
            title: {
              text: 'Average Salary',
              style: {
                color: '#1f1f1f',
                fontSize: '16px',
                fontFamily: 'Public Sans,sans-serif',
              },
            },
          },
          stroke: {
            show: true,
            curve: 'smooth',
            colors: ['#fff'],
            width: 3,
            dashArray: 0,
          },
          fill: {
            opacity: 0.6,
          },
          dataLabels: {
            enabled: false,
          },
          legend: {
            position: 'right',
            horizontalAlign: 'center',
            offsetX: -40,
            offsetY: 90,
            width: 320,
          },
          tooltip: {
            enabled: false,
          },
          chart: {
            events: {
              dataPointSelection(event, chartContext, config) {
                let chartDetails = {};
                chartDetails = {
                  id: config?.w.config.series[config?.seriesIndex]?.careers[config?.dataPointIndex]
                    .id,
                  title: config?.w.config.series[config?.seriesIndex].name,
                  courses:
                    config?.w.config.series[config?.seriesIndex]?.careers[config?.dataPointIndex]
                      .noOfCourse,
                  duration:
                    config?.w.config.series[config?.seriesIndex]?.careers[config?.dataPointIndex]
                      .transferWindow,
                  avgSalary: currencyFormat(
                    config?.w.config.series[config?.seriesIndex]?.data[config?.dataPointIndex][1]
                  ).toString(),
                  careerName:
                    config?.w.config.series[config?.seriesIndex]?.careers[config?.dataPointIndex]
                      .name,
                  popoverPosition: {
                    offsetX: event.offsetX,
                    offsetY: event.offsetY,
                  },
                  careerType: config?.w.config.series[config?.seriesIndex].careerType,
                  addToComparison:
                    config?.w.config.series[config?.seriesIndex]?.careers[config?.dataPointIndex]
                      .addToComparison,
                };
                careerChartNListData?.map((state) => {
                  state.careers.map((career) => {
                    if (chartDetails?.id === career.id) {
                      chartDetails.addToComparison = career.addToComparison;
                    }
                    return '';
                  });
                  return '';
                });
                setOnClickChartData(chartDetails);
              },
            },
          },
        },
      };
      setBubblechartData(data);
      const careerDataList = [];

      for (let i = 0; i < careerChartNListData?.length; i += 1) {
        for (let j = 0; j < careerChartNListData[i].careers?.length; j += 1) {
          careerDataList.push({
            name: careerChartNListData[i].name,
            careerType: careerChartNListData[i].careerType,
            color: careerChartNListData[i].color,
            id: careerChartNListData[i].careers[j].id,
            careerName: careerChartNListData[i].careers[j].name,
            noOfCourse: careerChartNListData[i].careers[j].noOfCourse,
            transferWindowRange: careerChartNListData[i].careers[j].transferWindow,
            transferWindow: careerChartNListData[i].data[j][0],
            avgSalary: careerChartNListData[i].data[j][1],
            addToComparison: careerChartNListData[i].careers[j].addToComparison,
          });
        }
      }
      if (onClickChartData) {
        const newChartData = onClickChartData;
        careerChartNListData.map((item) => {
          item.careers.map((career) => {
            if (career.id === newChartData.id) {
              newChartData.addToComparison = career.addToComparison;
            }
            return '';
          });
          return '';
        });
        setOnClickChartData(newChartData);
      }
      setReskillCareersListData(careerDataList);
    }
  }, [careerChartNListData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (onClickChartData) {
      showChartDataPopup();
    }
  }, [onClickChartData]);
  const redirectUrl = (data, type) => {
    let renderLink = '';
    if (data && data.length > 0) {
      const httpCheck = data.toLocaleLowerCase().startsWith('http');
      if (httpCheck) {
        renderLink = data;
      } else {
        renderLink = `http://${data}`;
      }
    }
    return (
      <a href={renderLink} rel="noreferrer" target="_blank">
        {' '}
        <Typography
          component="p"
          color="white"
          sx={{
            textTransform: 'none',
            display: 'flex',
            alignItems: 'center',
            fontSize: '1.05rem',
            padding: '3px',
          }}
        >
          {type}
          <KeyboardArrowRightIcon sx={{ color: 'white' }} />
        </Typography>
      </a>
    );
  };


    return (
      <AuthWrapper title="Widget">
        <DekstopPoup
          tabValue={tabValue}
          setTabValue={setTabValue}
          careerNCoursesPopDetails={careerNCoursesPopDetails}
          openCareerDetailsPopup={openCareerDetailsPopup}
          HandleCloseCareerDetailsPopup={HandleCloseCareerDetailsPopup}
          compareCareersState={compareCareersState}
          addToAnalytics={() => {}}
          redirectUrl={redirectUrl}
          currentNGoalCareerId={careerHistoryId}
          expanded={expanded}
          readMore={readMore}
          setReadMore={setReadMore}
          ref={ref}
          HandleOpenRadarPopup={HandleOpenRadarPopup}
          regionalChartData={regionalChartData}
          qualificationsChartData={qualificationsChartData}
          handleChange={handleChange}
          regionData={regionData}
          isLoading={careerNCoursesDetailsStatus?.isLoading}
          loadError={careerDetailsError}
          onRetry={() => setCareerDetailsRetry((count) => count + 1)}
        />
        <MobilePopup
          tabValue={tabValue}
          setTabValue={setTabValue}
          regionData={regionData}
          addToAnalytics={() => {}}
          isLoading={careerNCoursesDetailsStatus?.isLoading}
          loadError={careerDetailsError}
          onRetry={() => setCareerDetailsRetry((count) => count + 1)}
          careerNCoursesPopDetails={careerNCoursesPopDetails}
          openCareerDetailsPopup={openCareerDetailsPopup}
          HandleCloseCareerDetailsPopup={HandleCloseCareerDetailsPopup}
          redirectUrl={redirectUrl}
          expanded={expanded}
          readMore={readMore}
          setReadMore={setReadMore}
          ref={ref}
          HandleOpenRadarPopup={HandleOpenRadarPopup}
          regionalChartData={regionalChartData}
          qualificationsChartData={qualificationsChartData}
          handleChange={handleChange}
        />
        <Box
          sx={{
            backgroundImage: showBackground ? `url(${bgImage})` : 'none',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundAttachment: fixedLayout ? 'fixed' : 'scroll',
            // '&::-webkit-scrollbar': {
            //   display: 'none',
            // },
            // scrollbarWidth: 'none', // Firefox
            // msOverflowStyle: 'none', // IE 10+
            height: rootHeight,
            minHeight: autoHeight || configuredHeight ? 0 : '100vh',
            // stacked (narrow) layout with a set height: the content scrolls inside that height
            overflowY: configuredHeight && !fixedLayout ? 'auto' : 'visible',
            width: '100vw',
            position: fixedLayout ? 'fixed' : 'relative',
            top: 0,
            left: 0,
            zIndex: 1000,
          }}
          className={`page-content-wrapper tsw-root${autoHeight ? ' tsw-auto-height' : ''}${configuredHeight ? ' tsw-fixed-height' : ''}`}
          ref={setRootElement}
        >
          {customCss && <style data-college-css>{customCss}</style>}
          <Container
            maxWidth={false}
            disableGutters
            sx={{
              height: frameHeight,
              minHeight: frameMinHeight,
              width: '100vw',
              display: 'flex',
              flexDirection: 'column',
              ...framePadding,
            }}
          >
            <Box
              className="tsw-layout"
              sx={{
                display: 'flex',
                flexDirection: containerWidth > 1000 ? 'row' : 'column',
                flexGrow: 1,
                borderRadius: showBackground ? '8px' : 0,
                overflow: containerWidth > 1000 ? 'hidden' : 'visible',
                height: fillMain ? '100%' : 'auto',
                gap: containerWidth > 1000 ? 5 : 0,
              }}
            >
              <Box
                className="tsw-main"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: leftColumnFlex,
                  minHeight: fillMain ? '100%' : 'auto',
                  height: fillMain ? '100%' : 'auto',
                }}
              >
                <WidgetStepper currentStep={currentStep} onStepChange={handleStepChange} showVideoTab={showVideoTab} showChatTab={showChatTab} />
                <Box
                  sx={{
                    flexGrow: fillMain ? 1 : 0,
                    overflowY: fillMain ? 'auto' : 'visible',
                    px: 0,
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    bgcolor: 'white',
                    height: fillMain ? '100%' : 'auto',
                  }}
                >
                  <Box
                    sx={{
                      overflowY: 'auto',
                      flexGrow: 1,
                      px: 2,
                      py: 1,
                      '&::-webkit-scrollbar': { display: 'none' },
                      display: currentStep === 0 ? 'block' : 'none',
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none',
                      height: fillMain ? '100%' : 'auto',
                    }}
                  >
                    {careersReskillTime?.sectorOrSubsector?.length > 0 && (
                      <>
                        {!status?.isLoading && !careerLoading && (
                          <Typography
                            variant="h1"
                            sx={{
                              fontWeight: 700,
                              textAlign: 'left',
                              py: 2,
                              fontSize: '16px !important',
                            }}
                          >
                            {careersReskillTime?.sectorOrSubsector[0].name} students can go onto the
                            following careers.
                          </Typography>
                        )}
                        <BarsComponent
                          data={careersReskillTime?.sectorOrSubsector}
                          Careers={careersReskillTime?.sectorOrSubsector}
                          showCareerDetailsPopup={showCareerDetailsPopup}
                          widthClass="bar-wrapper"
                          isWidget
                          onWidgetCareerClick={() => {}}
                        />
                      </>
                    )}
                    {status?.isLoading ||
                      (careerLoading && (
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <CircularProgress />
                        </Box>
                      ))}
                  </Box>
                  <Box
                    sx={{
                      flexGrow: fillMain ? 1 : 0,
                      overflow: fillMain ? 'auto' : 'visible',
                      display: currentStep === 1 ? 'block' : 'none',
                      height: fillMain ? '100%' : 'auto',
                    }}
                  >
                    {showVideoTab && <VideoComponent key={sectorVideoUrls.join('|')} url={sectorVideoUrls} />}
                  </Box>
                  <Box
                    sx={{
                      flexGrow: fillMain ? 1 : 0,
                      overflow: fillMain ? 'auto' : 'visible',
                      display: currentStep === 2 ? 'block' : 'none',
                      height: fillMain ? '100%' : 'auto',
                    }}
                  >
                    {showChatTab && (
                      <ChatBot
                        clgBySlug={clgBySlug}
                        regionData={regionData}
                        currentStep={currentStep}
                        fillHeight={fillMain}
                        pageURL={careerurl}
                        courseId={courseId || undefined}
                      />
                    )}
                  </Box>
                </Box>
              </Box>

              {showSkilldar && (
              <Box
                className="tsw-skilldar"
                sx={{
                  flex: containerWidth > 1000 ? '1 1 30%' : '0 0 auto',
                  minHeight: fixedLayout ? '100%' : 'auto',
                  height: fixedLayout ? '100%' : 'auto',
                  backgroundColor: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  borderLeft: containerWidth > 1000 ? '1px solid #0a1760' : 'none',
                  borderTop: containerWidth <= 1000 ? '1px solid #0a1760' : 'none',
                  overflow: fixedLayout ? 'scroll' : 'visible',
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
              >
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: containerWidth > 1000 ? '0' : '12px',
                    width: '100%',
                    height: fixedLayout ? '100%' : 'auto',
                    textAlign: 'center',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography
                    variant="h2"
                    fontWeight="bold"
                    gutterBottom
                    sx={{
                      fontSize: '18px !important',
                    }}
                  >
                    ThinkSkills Skilldar
                  </Typography>

                  <Typography component="p" color="textSecondary" gutterBottom>
                    {careersReskillTime?.sectorName
                      ? `This skilldar shows the mix of skills needed to work in ${careersReskillTime.sectorName}`
                      : 'This skilldar shows the mix of skills needed for these careers'}
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      flex: fixedLayout ? 1 : 'none',
                      minHeight: containerWidth > 1000 ? 300 : 'auto',
                      height: containerWidth <= 1000 ? 'auto' : undefined,
                    }}
                  >
                    {careersReskillTime?.sectorOrSubsector?.length > 0 ? (
                      <WidgetRadarChart
                        containerWidth={containerWidth}
                        careersReskillTime={careersReskillTime}
                        onDataLoaded={(data) => setWidgetRadarData(data)}
                        showLabel={showLabel}
                        openLabel={openLabel}
                        closeLabel={closeLabel}
                      />
                    ) : (
                      <CircularProgress />
                    )}
                  </Box>

                  <Button
                    variant="outlined"
                    fullWidth
                    sx={{
                      borderRadius: '12px',
                      textTransform: 'none',
                      borderColor: '#000',
                      color: '#000',
                      fontWeight: 'bold',
                      px: 2,
                      py: 2,
                      mt: 2,
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                        borderColor: '#000',
                      },
                      marginBottom: '15px !important',
                    }}
                  >
                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        flexDirection: containerWidth > 1000 ? 'column' : 'row',
                        textAlign: {
                          xs: 'center',
                          sm: 'left',
                        },
                        width: '100%',
                      }}
                    >
                      <Grid item xs>
                        <Typography variant="body1" fontWeight="bold" sx={{ lineHeight: 1.4 }}>
                          See if your skills match!
                        </Typography>
                      </Grid>

                      {containerWidth <= 1000 && (
                        <Grid
                          item
                          sx={{
                            height: '100%',
                            borderLeft: '1px solid black',
                          }}
                        />
                      )}

                      <Grid item xs>
                        <Typography variant="body1">
                          Visit <b>Think</b>Skills{regionName ? `:${regionName}` : ''}
                          <br />& take our skills test
                        </Typography>
                      </Grid>
                    </Grid>
                  </Button>
                </Box>
              </Box>
              )}
            </Box>
            {showPoweredBy && (
              <Box
                className="tsw-powered-by"
                sx={{
                  width: '100%',
                  px: 2,
                  position: fixedLayout ? 'absolute' : 'relative',
                  bottom: fixedLayout ? 7 : 0,
                  left: fixedLayout ? 30 : 0,
                  mt: !fixedLayout ? 2 : 0,
                }}
              >
                <Typography variant="caption" textAlign="left" sx={{ mt: 1.5, color: '#f9a748' }}>
                  Powered by ThinkSkills{regionName ? `: ${regionName}` : ''}
                </Typography>
              </Box>
            )}
          </Container>

          <Popover open={skillRadarPopup} onClose={HandleCloseRadarPopup} className="chart-popup">
            <Button className="close-btn" onClick={HandleCloseRadarPopup}>
              <CloseIcon />
            </Button>

            <Box className="col">
              <Typography
                variant="h2"
                className="heading"
                color="primary.dark"
                fontSize="1.5rem !important"
              >
                Skilldar
              </Typography>
              <Grid container gap={2} flexWrap="nowrap" className="popup-chart-wrapper">
                <Grid item lg={3}>
                  <Box className="skilldar-keys">
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        className="title"
                        variant="capitalize"
                        textAlign="left"
                        color="#000c3b"
                        sx={{ marginBottom: '8px' }}
                      >
                        Skill Level
                      </Typography>
                      {skillLevels.map((skill) => (
                        <Typography
                          key={skill.title}
                          sx={{ marginBottom: '0px !important' }}
                          className="skill-level"
                          color="#000c3b"
                        >
                          <span
                            className="line"
                            style={{
                              borderColor: skill.color,
                              width: '15%',
                              display: 'inline-block',
                            }}
                          />
                          {skill.title}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                </Grid>
                <Grid item lg={9} ref={popupRef}>
                  <RadarChartComponent
                    width={popupChartWidth || 400}
                    legend
                    radarApiDetails={radarPopupData}
                    height={500}
                    careersDetails
                    params={params}
                    showlabels
                    disableTooltip
                    chartAnimation
                  />
                </Grid>
              </Grid>
            </Box>
          </Popover>
        </Box>
      </AuthWrapper>
    );
};

export default Widget;
